import React from 'react';
import { useCanReadCustomWorkspace } from 'abilities/workspaces';
import { useDriveQuery } from '../../Workspaces/operation.generated';
import {
  useAncestorFoldersQuery,
  useFolderQuery,
} from '../../Folders/operation.generated';
import { UserProjectQuery } from '../../Projects/operation.generated';
import { getDriveTypeFromName } from '../../Drives/utils';
import { DriveType } from '../../Drives/types';

/**
 * The slice of the Studio project the trail needs. Kept structural so both the
 * user project and any equivalently shaped project can be passed in.
 */
export type StudioBreadcrumbProject = Pick<
  NonNullable<UserProjectQuery['user']['project']>,
  'id' | 'name' | 'folderId' | 'rootDriveId'
>;

export interface StudioBreadcrumbTrailItem {
  id: string;
  itemLabel: string;
  href?: string;
}

/**
 * The project crumb's own route. It is the page the user is already on, so it
 * is never navigated to — but `FoldersNavBreadcrumb` marks a crumb inactive
 * only when its `href` matches the container's `currentHref`, so the crumb
 * needs a self-referential href to render disabled rather than as an enabled
 * button. The consumer passes this same value as `currentHref`.
 */
export const studioProjectHref = (projectId: string) =>
  `/dashboard/studio/${projectId}`;

interface StudioBreadcrumbTrail {
  /**
   * Ordered workspace -> folders -> project crumbs. Empty while resolving so
   * the header never renders a partial trail.
   */
  items: StudioBreadcrumbTrailItem[];
  loading: boolean;
  /**
   * True when the trail collapsed to the project name alone, i.e. there is no
   * workspace destination to link to (shared-with-me projects) or a query
   * failed.
   */
  isDegraded: boolean;
}

/**
 * Resolves the breadcrumb trail for the project open in Studio.
 *
 * The queries live here rather than in the Studio page's loading gate, and read
 * `cache-first`, so opening a project never waits on the trail and a revisit
 * renders an already-known trail without a placeholder.
 *
 * Any drive or folder failure degrades to the project name alone rather than
 * rendering a crumb that goes nowhere: both the drive query (RBAC) and the
 * ancestor query (folder permission check) throw for users who can legitimately
 * open the project.
 */
const useStudioBreadcrumbTrail = ({
  project,
}: {
  project: StudioBreadcrumbProject;
}): StudioBreadcrumbTrail => {
  const canReadCustomWorkspace = useCanReadCustomWorkspace();

  const { rootDriveId, folderId } = project;

  /**
   * Skips key off `rootDriveId`, not `driveId`: a shared-with-me project keeps a
   * populated `driveId` while having no workspace of the viewer's own to link
   * to, so a `driveId` keyed skip would never fire for it.
   */
  const {
    data: driveData,
    loading: driveLoading,
    error: driveError,
  } = useDriveQuery({
    skip: !rootDriveId,
    variables: {
      inputs: {
        driveId: rootDriveId!,
      },
    },
    fetchPolicy: 'cache-first',
  });

  /**
   * `ancestorFolders` excludes the folder itself - it drops the self
   * referential hierarchy row - so the folder holding the project is fetched
   * separately, the same way `FolderNavbarBreadcrumbs` appends its current
   * folder after the ancestors.
   */
  const {
    data: folderData,
    loading: folderLoading,
    error: folderError,
  } = useFolderQuery({
    skip: !rootDriveId || !folderId,
    variables: {
      inputs: {
        folderId: folderId!,
      },
    },
    fetchPolicy: 'cache-first',
  });

  const {
    data: ancestorsData,
    loading: ancestorsLoading,
    error: ancestorsError,
  } = useAncestorFoldersQuery({
    skip: !rootDriveId || !folderId,
    variables: {
      inputs: {
        folderId: folderId!,
      },
    },
    fetchPolicy: 'cache-first',
  });

  const loading = driveLoading || folderLoading || ancestorsLoading;

  const drive = driveData?.drive;
  const folder = folderData?.folder;
  const driveType = drive ? getDriveTypeFromName(drive) : undefined;

  const hasError = Boolean(driveError || folderError || ancestorsError);

  const isDegraded =
    !loading &&
    (hasError || !drive || !driveType || Boolean(folderId && !folder));

  const ancestorFolders = React.useMemo(() => {
    if (!ancestorsData?.ancestorFolders) return [];

    return [...ancestorsData.ancestorFolders].sort((a, b) => {
      return a.sequence - b.sequence;
    });
  }, [ancestorsData?.ancestorFolders]);

  const items = React.useMemo(() => {
    const projectItem: StudioBreadcrumbTrailItem = {
      id: project.id,
      itemLabel: project.name,
      href: studioProjectHref(project.id),
    };

    if (loading) return [];
    if (isDegraded || !drive || !driveType) return [projectItem];

    const baseRoute = canReadCustomWorkspace
      ? '/dashboard/workspaces/'
      : '/dashboard/projects/';

    const routeId = canReadCustomWorkspace
      ? drive.id
      : drive.name.toLowerCase();

    const workspaceItem: StudioBreadcrumbTrailItem =
      driveType === DriveType.Custom
        ? {
            id: drive.id,
            itemLabel: drive.name,
            href: `${baseRoute}${drive.id}`,
          }
        : {
            id: drive.id,
            itemLabel: driveType,
            href: `${baseRoute}${routeId}`,
          };

    const folderItems: StudioBreadcrumbTrailItem[] = ancestorFolders.map(
      ancestorFolder => ({
        id: ancestorFolder.id,
        itemLabel: ancestorFolder.name,
        href: `${baseRoute}${routeId}/folders/${ancestorFolder.id}`,
      })
    );

    if (folder) {
      folderItems.push({
        id: folder.id,
        itemLabel: folder.name,
        href: `${baseRoute}${routeId}/folders/${folder.id}`,
      });
    }

    return [workspaceItem, ...folderItems, projectItem];
  }, [
    ancestorFolders,
    canReadCustomWorkspace,
    drive,
    driveType,
    folder,
    isDegraded,
    loading,
    project.id,
    project.name,
  ]);

  return {
    items,
    loading,
    isDegraded,
  };
};

export default useStudioBreadcrumbTrail;
